import { useEffect, useState } from 'react';

import { checkPasswordResetLink, confirmPasswordReset } from '../../api/auth';

/**
 * "Choose a new password" step, opened from the link in the reset email.
 * uid/token come from that link (see Shell in App.js). The link is checked
 * up front so an expired/used link doesn't show the form at all.
 */
export default function SetPasswordView({ onSwitch, resetLink }) {
  const [linkStatus, setLinkStatus] = useState('checking'); // 'checking' | 'valid' | 'invalid'
  const [linkError, setLinkError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    checkPasswordResetLink(resetLink)
      .then(() => {
        if (active) setLinkStatus('valid');
      })
      .catch((err) => {
        if (!active) return;
        setLinkError(err.message || 'Something went wrong. Please try again.');
        setLinkStatus('invalid');
      });
    return () => {
      active = false;
    };
  }, [resetLink]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords don’t match.');
      return;
    }

    setSubmitting(true);
    try {
      await confirmPasswordReset({ ...resetLink, password });
      setDone(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (linkStatus === 'checking') {
    return (
      <div className="auth-panel">
        <h2 className="auth-title">Set new password</h2>
        <p className="auth-sub">Checking your link…</p>
      </div>
    );
  }

  if (linkStatus === 'invalid') {
    return (
      <div className="auth-panel">
        <h2 className="auth-title">Link not valid</h2>
        <div className="auth-error">{linkError}</div>
        <button className="auth-submit" type="button" onClick={() => onSwitch('reset')}>
          Send a New Link
        </button>
      </div>
    );
  }

  if (done) {
    return (
      <div className="auth-panel">
        <h2 className="auth-title">Password updated</h2>
        <div className="auth-success">Your password has been changed. You can now log in with your new password.</div>
        <button className="auth-submit" type="button" onClick={() => onSwitch('login')}>
          Log In
        </button>
      </div>
    );
  }

  return (
    <form className="auth-panel" onSubmit={handleSubmit} noValidate>
      <h2 className="auth-title">Set new password</h2>
      <p className="auth-sub">Choose a new password for your account</p>

      {error && <div className="auth-error">{error}</div>}

      <div className="field">
        <label htmlFor="set-password">New password</label>
        <input
          type="password"
          id="set-password"
          placeholder="Min. 8 characters"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      </div>

      <div className="field">
        <label htmlFor="set-password-confirm">Confirm password</label>
        <input
          type="password"
          id="set-password-confirm"
          placeholder="Type it again"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
        />
      </div>

      <button className="auth-submit" type="submit" disabled={submitting}>
        {submitting ? 'Saving…' : 'Save Password'}
      </button>

      <p className="auth-switch">
        Link expired?{' '}
        <a href="#reset" onClick={(e) => { e.preventDefault(); onSwitch('reset'); }}>
          Send a new one
        </a>
      </p>
    </form>
  );
}
